import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useRestoreContactsMutation } from "../../../../../../features/contactsApi"
import { addBannerWithTimeout } from "../../../../../lib/centralizedBanner/slice"
import { ContactType, FindAllContactsFilters, FindContactsTargetResponse } from "../../../../../lib/filters/dto"
import { keycloak } from "../../../../../lib/keycloak"
import { useAppDispatch, useAppSelector } from "../../../../../lib/redux/hooks"
import { DownloadIcon } from "../../../../../ui/icons/download"
import { PlusIcon } from "../../../../../ui/icons/plus"
import { TrashIcon } from "../../../../../ui/icons/trash"
import { UserPlusIcon } from "../../../../../ui/icons/userPlus"
import Button from "../../../../../ui/molecules/button"
import ButtonDropdown from "../../../../../ui/molecules/buttonDropdown"
import { Spinner } from "../../../../../ui/molecules/spinner"
import { DownloadBulk } from "../../../../database/commons/downloadBulk"
import { useDownloadCsv } from "../../../../database/commons/useDownloadCsv"
import { TargetListDTO, TargetListPageEnum } from "../../../dto"
import { setTargetListPage } from "../../../slice"

interface Props {
    isInBin: boolean
    selectedIds: { contactId: string, personId: string }[]
    setSelectedIds: (ids: { contactId: string, personId: string }[]) => void
    setShowPopup: (show: boolean) => void
    filters: FindAllContactsFilters
    setFilters: (filters: FindAllContactsFilters) => void
    contacts: FindContactsTargetResponse<ContactType.PERSON>
    targetList: TargetListDTO
    setDeleteBulkContactsModal: (show: boolean) => void
    setShowSoftBulkModal: (show: boolean) => void
}

export function ActionsPeopleTargetList(props: Props) {
    const projectId = useParams().projectId!
    const { selectedIds, setShowPopup, filters, contacts, setSelectedIds, setFilters, setShowSoftBulkModal, targetList, isInBin, setDeleteBulkContactsModal } = props
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const usersState = useAppSelector(state => state.users)
    const [restoreContacts] = useRestoreContactsMutation()

    const downloadPeopleCsv = useDownloadCsv(selectedIds.map(id => id.personId), () => setSelectedIds([]), ContactType.PERSON);
    const [showDownloadBulkContactsModal, setShowDownloadBulkContactsModal] = useState(false);

    const handleRestoreContacts = async (ids: string[]) => {
        try {
            await restoreContacts({ contacts: ids, targetList: targetList.id }).unwrap();
            dispatch(addBannerWithTimeout({
                message: 'Contatti ripristinati con successo.',
                variant: 'success'
            }))
        } catch (error) {
            dispatch(addBannerWithTimeout({
                message: 'Si è verificato un errore durante il ripristino dei contatti.',
                variant: 'error'
            }))
        }
    }

    return (
        <div className="flex items-center gap-2">
            <DownloadBulk
                contactType={ContactType.PERSON}
                section="target"
                filters={filters}
                setFilters={(filters) => setFilters(filters)}
                showModal={showDownloadBulkContactsModal}
                setShowModal={setShowDownloadBulkContactsModal}
            />
            {isInBin && selectedIds.length > 0 &&
                !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
                <Button
                    size={"sm"}
                    label="Ripristina"
                    iconPosition={"left"}
                    variant={"outline"}
                    color={"blue"}
                    icon={<UserPlusIcon color={""} size={0} />}
                    onClick={() => handleRestoreContacts(selectedIds.map(id => id.contactId))}
                />
            }
            {
                !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
                !isInBin &&
                <ButtonDropdown
                    options={[
                        {
                            disabled: (
                                (keycloak.hasRealmRole('commercial') && !usersState.findMeResponse?.isAutonomous) ||
                                (keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator'))
                            ),
                            label: 'Cestina tutti',
                            onClick: () => setShowSoftBulkModal(true)
                        }
                    ]}
                    size={"sm"}
                    iconPosition={"left"}
                    color={"blue"}
                    onClick={() => {
                        navigate('/target-list-bins/' + targetList.id + '/people/' + projectId)
                    }}
                    label="Visualizza Ko"
                    icon={<TrashIcon color={""} size={0} />}
                />
            }
            {
                (
                    keycloak.hasRealmRole('admin') ||
                    (keycloak.hasRealmRole('commercial') && usersState.findMeResponse?.isAutonomous) ||
                    keycloak.hasRealmRole('team_leader')
                ) && contacts.total !== 0 &&
                (downloadPeopleCsv.loading ?
                    <Spinner /> :
                    <ButtonDropdown
                        disabled={selectedIds.length <= 0}
                        options={[
                            {
                                label: 'Esporta tutti',
                                onClick: () => {
                                    setShowDownloadBulkContactsModal(true)
                                }
                            }
                        ]}
                        size={"sm"}
                        iconPosition={"left"}
                        color={"blue"}
                        onClick={() => downloadPeopleCsv.handleDownloadConfirm()}
                        label="Esporta"
                        icon={<DownloadIcon color={""} size={0} />}
                    />)
            }
            {
                !(
                    keycloak.hasRealmRole('customer') ||
                    keycloak.hasRealmRole('collaborator') ||
                    keycloak.hasRealmRole('commercial')
                )
                && contacts?.total !== 0 &&
                <ButtonDropdown
                    disabled={selectedIds.length <= 0}
                    icon={<TrashIcon color={""} size={0} />}
                    options={[
                        {
                            label: 'Elimina tutti',
                            onClick: () => setDeleteBulkContactsModal(true)
                        }
                    ]}
                    onClick={() => setShowPopup(true)}
                    label="Elimina"
                    size={"sm"}
                    iconPosition={"left"}
                    color={"blue"}
                />
            }
            {
                !(keycloak.hasRealmRole('commercial') && !usersState.findMeResponse?.isAutonomous) &&
                !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) &&
                <Button
                    size={"sm"}
                    iconPosition={"left"}
                    variant={"solid"}
                    color={"blue"}
                    icon={<PlusIcon color={""} size={0} />}
                    label={
                        "Aggiungi persone"
                    }
                    onClick={() =>
                        dispatch(setTargetListPage(keycloak.hasRealmRole('commercial') ? TargetListPageEnum.DB : TargetListPageEnum.ADD))
                    }
                />
            }
        </div>
    )
}